import { useState, useEffect, useCallback, useRef } from 'react';
import { useAdaptivePoll } from '@shared/hooks/useAdaptivePoll';
import { useTranslation } from 'react-i18next';
import { formatBytes } from '@/shared/utils/formatBytes';

interface BackupFile {
  path: string;
  name: string;
  type: 'file' | 'directory';
  size?: number;
  mode?: string;
  modified?: string;
  parent_path?: string;
}

interface BrowseBackupResponse {
  backup_id: string;
  backup_name: string;
  backup_type: string;
  current_path: string;
  files: BackupFile[];
  parent_path?: string;
}

interface SearchBackupResponse {
  backup_id: string;
  query: string;
  results: BackupFile[];
  total_results: number;
}

interface FileIndexStatusResponse {
  file_index_status: string | null;
  total_files: number;
  total_dirs: number;
  total_size: number;
}

interface BackupFileBrowserProps {
  isOpen: boolean;
  onClose: () => void;
  backupJobId: string;
  backupConfigName: string;
  initialSelectedFiles?: string[];
  onSelectFiles: (files: string[]) => void;
}

export const BackupFileBrowser = ({
  isOpen,
  onClose,
  backupJobId,
  backupConfigName,
  initialSelectedFiles = [],
  onSelectFiles,
}: BackupFileBrowserProps) => {
  const { t, i18n } = useTranslation(['backups', 'common']);
  const [currentPath, setCurrentPath] = useState('/');
  const [files, setFiles] = useState<BackupFile[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<string[]>(() => [...initialSelectedFiles]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [searchResults, setSearchResults] = useState<BackupFile[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [isSearchMode, setIsSearchMode] = useState(false);

  // Index status state
  const [indexStatus, setIndexStatus] = useState<string | null>(null);
  // Read inside the adaptive poll's fetcher so the poll is not recreated on
  // every response.
  const indexStatusRef = useRef<string | null>(null);
  const [indexStats, setIndexStats] = useState<FileIndexStatusResponse | null>(null);

  const isIndexCompleted = indexStatus === 'completed';
  const isSearchDisabled = !isIndexCompleted;

  const fetchFiles = useCallback(
    async (path: string) => {
      setLoading(true);
      setError('');

      try {
        const token = localStorage.getItem('auth_token');
        const response = await fetch(
          `/api/v1/restore/browse/${backupJobId}?path=${encodeURIComponent(path)}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error(t('fileBrowser.errors.browseFailed'));
        }

        const data: BrowseBackupResponse = await response.json();
        setFiles(data.files);
        setCurrentPath(data.current_path);
      } catch {
        setError(t('fileBrowser.errors.loadFailed'));
      } finally {
        setLoading(false);
      }
    },
    [backupJobId, t]
  );

  const fetchIndexStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/v1/restore/browse/${backupJobId}/status`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        return;
      }

      const data: FileIndexStatusResponse = await response.json();
      setIndexStatus(data.file_index_status);
      setIndexStats(data);
    } catch {
      // Silently fail — browse still works without index status
    }
  }, [backupJobId]);

  // Keep the poll's ref pointing at the latest value.
  indexStatusRef.current = indexStatus;
  const fetchSearchResults = useCallback(
    async (query: string) => {
      setSearchLoading(true);
      setError('');

      try {
        const token = localStorage.getItem('auth_token');
        const response = await fetch(
          `/api/v1/restore/browse/${backupJobId}/search?q=${encodeURIComponent(query)}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error(t('fileBrowser.errors.searchFailed'));
        }

        const data: SearchBackupResponse = await response.json();
        setSearchResults(data.results);
      } catch {
        setError(t('fileBrowser.errors.searchFailed'));
      } finally {
        setSearchLoading(false);
      }
    },
    [backupJobId, t]
  );

  // Fetch files when path changes (browse mode)
  useEffect(() => {
    if (isOpen && backupJobId && !isSearchMode) {
      fetchFiles(currentPath);
    }
  }, [isOpen, backupJobId, currentPath, isSearchMode, fetchFiles]);

  // Poll the index build until it settles. Adaptive: 5s base backing off to 30s
  // while the status is unchanged, and paused while the tab is hidden.
  useAdaptivePoll({
    enabled: isOpen && !!backupJobId && indexStatus !== 'completed' && indexStatus !== 'failed',
    baseIntervalMs: 5000,
    maxIntervalMs: 30000,
    fetcher: async () => {
      await fetchIndexStatus();
      return indexStatusRef.current;
    },
    isEqual: (a, b) => a === b,
  });

  // Debounce search query (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Execute search when debounced query changes
  useEffect(() => {
    if (debouncedQuery.trim() && isOpen && backupJobId && isIndexCompleted) {
      setIsSearchMode(true);
      fetchSearchResults(debouncedQuery.trim());
    } else if (!debouncedQuery.trim() && isSearchMode) {
      // Cleared search — return to browse mode
      setIsSearchMode(false);
      setSearchResults([]);
    }
  }, [debouncedQuery, isOpen, backupJobId, isIndexCompleted, isSearchMode, fetchSearchResults]);

  const handleClearSearch = () => {
    setSearchQuery('');
    setDebouncedQuery('');
    setIsSearchMode(false);
    setSearchResults([]);
  };

  const handleNavigateToPath = (path: string) => {
    setCurrentPath(path);
  };

  const handleNavigateUp = () => {
    if (currentPath !== '/' && currentPath !== 'C:\\' && currentPath !== 'C:/') {
      const normalizedPath = currentPath.replace(/\\/g, '/');
      const parts = normalizedPath.split('/').filter(Boolean);
      parts.pop();
      const parentPath = '/' + parts.join('/');
      setCurrentPath(parentPath === '/' ? '/' : parentPath);
    }
  };

  const toggleFileSelection = (path: string) => {
    setSelectedFiles((prev) =>
      prev.includes(path) ? prev.filter((p) => p !== path) : [...prev, path]
    );
  };

  const handleConfirmSelection = () => {
    if (selectedFiles.length > 0) {
      onSelectFiles(selectedFiles);
    }
  };

  const formatTimestamp = (timestamp: string | undefined) => {
    if (!timestamp) return '-';
    try {
      const date = new Date(timestamp);
      return new Intl.DateTimeFormat(i18n.resolvedLanguage || i18n.language, {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date);
    } catch {
      return timestamp;
    }
  };

  if (!isOpen) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="backup-file-browser-title"
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[110] animate-in fade-in duration-200"
    >
      <div className="bg-surface-card-light dark:bg-surface-card-dark rounded-2xl shadow-elevated max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col border border-surface-border-light dark:border-surface-border-dark">
        {/* Header */}
        <div className="p-4 sm:p-6 border-b border-surface-border-light dark:border-surface-border-dark flex-shrink-0 bg-surface-2-light dark:bg-surface-2-dark">
          <div className="flex items-center justify-between">
            <div>
              <h2
                id="backup-file-browser-title"
                className="text-lg sm:text-xl font-black text-slate-900 dark:text-white tracking-tight uppercase tracking-wider"
              >
                {t('fileBrowser.title')}
              </h2>
              <p className="text-sm text-slate-600 dark:text-text-subtle-dark mt-1 font-medium">
                {t('fileBrowser.backupLabel')}{' '}
                <span className="font-bold text-slate-900 dark:text-white">{backupConfigName}</span>
              </p>
            </div>
            <button
              type="button"
              aria-label={t('common:actions.close')}
              title={t('common:actions.close')}
              onClick={onClose}
              className="min-h-11 min-w-11 flex items-center justify-center text-text-subtle-light dark:text-text-subtle-dark hover:text-text-muted-light dark:hover:text-slate-300 p-1 rounded-lg hover:bg-surface-2-light dark:hover:bg-surface-2-dark transition-colors"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Search Bar */}
          <div className="mt-4 relative">
            <div className="relative">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-subtle-light dark:text-text-subtle-dark"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                type="text"
                aria-label={t('fileBrowser.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('fileBrowser.searchPlaceholder')}
                disabled={isSearchDisabled}
                className="min-h-11 w-full pl-10 pr-12 py-2.5 border border-surface-border-light dark:border-surface-border-dark rounded-xl bg-surface-card-light dark:bg-surface-card-dark text-sm text-slate-900 dark:text-slate-200 placeholder:text-text-subtle-light dark:placeholder:text-text-subtle-dark focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              />
              {searchQuery && (
                <button
                  type="button"
                  aria-label={t('fileBrowser.searchClear')}
                  onClick={handleClearSearch}
                  className="absolute right-1 top-1/2 -translate-y-1/2 min-h-11 min-w-11 inline-flex items-center justify-center text-text-subtle-light dark:text-text-subtle-dark hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                  title={t('fileBrowser.searchClear')}
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
            {isSearchDisabled && indexStatus !== null && (
              <p className="mt-1.5 text-xs text-text-muted-light dark:text-text-muted-dark font-medium">
                {indexStatus === 'indexing' && t('fileBrowser.indexNotReady')}
                {indexStatus === 'failed' && t('fileBrowser.indexFailed')}
                {(indexStatus === 'pending' || indexStatus === null) &&
                  t('fileBrowser.indexPending')}
              </p>
            )}
          </div>

          {/* Index Stats Bar (when completed) */}
          {isIndexCompleted && indexStats && (
            <div className="mt-3 grid grid-cols-3 gap-2 text-xs text-text-muted-light dark:text-text-muted-dark font-medium">
              <span className="min-w-0 leading-4">
                <span className="block">{t('fileBrowser.indexedFiles')}</span>
                <span className="font-bold text-slate-700 dark:text-slate-300">
                  {indexStats.total_files}
                </span>
              </span>
              <span className="min-w-0 leading-4">
                <span className="block">{t('fileBrowser.indexedFolders')}</span>
                <span className="font-bold text-slate-700 dark:text-slate-300">
                  {indexStats.total_dirs}
                </span>
              </span>
              <span className="min-w-0 leading-4">
                <span className="block">{t('fileBrowser.totalSize')}</span>
                <span className="font-bold text-slate-700 dark:text-slate-300">
                  {formatBytes(indexStats.total_size)}
                </span>
              </span>
            </div>
          )}

          {/* Index Status Warnings */}
          {indexStatus === 'failed' && (
            <div className="mt-3 bg-state-warn-bg-light dark:bg-state-warn-bg-dark border border-state-warn-light/20 dark:border-state-warn-dark/20 rounded-xl p-3">
              <p className="text-xs font-bold text-state-warn-light dark:text-state-warn-dark">
                {t('fileBrowser.indexFailed')}
              </p>
            </div>
          )}
          {indexStatus === 'indexing' && (
            <div className="mt-3 bg-state-info-bg-light dark:bg-state-info-bg-dark border border-state-info-light/20 dark:border-state-info-dark/20 rounded-xl p-3">
              <p className="text-xs font-bold text-state-info-light dark:text-state-info-dark">
                {t('fileBrowser.indexNotReady')}
              </p>
            </div>
          )}
          {indexStatus === 'pending' && (
            <div className="mt-3 bg-surface-2-light dark:bg-surface-2-dark border border-surface-border-light dark:border-surface-border-dark rounded-xl p-3">
              <p className="text-xs font-bold text-slate-600 dark:text-text-subtle-dark">
                {t('fileBrowser.indexPending')}
              </p>
            </div>
          )}

          {/* Breadcrumb Navigation — hidden in search mode */}
          {!isSearchMode && (
            <div className="mt-4 flex items-center space-x-2 flex-wrap">
              <button
                type="button"
                onClick={() => setCurrentPath('/')}
                className="min-h-11 px-2 text-sm text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 font-bold"
              >
                {t('fileBrowser.root')}
              </button>
              {currentPath !== '/' && currentPath !== 'C:\\' && currentPath !== 'C:/' && (
                <>
                  <span className="text-text-subtle-light dark:text-text-subtle-dark">/</span>
                  {currentPath
                    .replace(/\\/g, '/')
                    .split('/')
                    .filter(Boolean)
                    .map((part, idx, arr) => (
                      <span key={idx} className="flex items-center">
                        <button
                          onClick={() => {
                            const newPath = '/' + arr.slice(0, idx + 1).join('/');
                            setCurrentPath(newPath);
                          }}
                          className="text-sm text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 font-medium"
                        >
                          {part}
                        </button>
                        {idx < arr.length - 1 && (
                          <span className="text-text-subtle-light dark:text-text-subtle-dark mx-1">
                            /
                          </span>
                        )}
                      </span>
                    ))}
                </>
              )}
            </div>
          )}
        </div>

        {/* File List */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-6 bg-surface-card-light dark:bg-surface-card-dark">
          {error && (
            <div className="mb-4 bg-state-danger-bg-light dark:bg-state-danger-bg-dark border border-state-danger-light/20 dark:border-state-danger-dark/20 rounded-xl p-4">
              <p
                role="alert"
                className="text-sm font-bold text-state-danger-light dark:text-state-danger-dark"
              >
                {error}
              </p>
              <button
                type="button"
                onClick={() =>
                  isSearchMode ? fetchSearchResults(searchQuery) : fetchFiles(currentPath)
                }
                className="mt-2 min-h-11 rounded-lg px-3 text-sm font-semibold text-state-danger-light underline hover:no-underline dark:text-state-danger-dark"
              >
                {t('fileBrowser.retry')}
              </button>
            </div>
          )}

          {/* Search Mode */}
          {isSearchMode ? (
            <div>
              {/* Search Results Summary */}
              {searchLoading ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
                  <p className="mt-2 text-sm text-text-muted-light dark:text-text-muted-dark font-medium">
                    {t('fileBrowser.searchLoading')}
                  </p>
                </div>
              ) : (
                <>
                  {searchResults.length > 0 && (
                    <p className="mb-3 text-sm font-bold text-slate-700 dark:text-slate-300">
                      {t('fileBrowser.searchResults', { count: searchResults.length })}
                    </p>
                  )}
                  <div className="overflow-hidden border border-surface-border-light dark:border-surface-border-dark rounded-xl bg-surface-2-light dark:bg-surface-2-dark">
                    {searchResults.length === 0 ? (
                      <div className="text-center py-8 text-text-muted-light dark:text-text-muted-dark font-medium">
                        {t('fileBrowser.searchNoResults')}
                      </div>
                    ) : (
                      searchResults.map((file) => (
                        <div
                          key={file.path}
                          className="flex min-h-11 sm:min-h-7 min-w-0 items-center gap-2 sm:gap-3 px-2 sm:px-3 py-0 border-b border-surface-border-light dark:border-surface-border-dark last:border-b-0 hover:bg-surface-2-light dark:hover:bg-surface-border-dark transition-colors"
                          onClick={() => toggleFileSelection(file.path)}
                        >
                          {file.type === 'directory' ? (
                            <>
                              <label
                                className="min-h-11 sm:min-h-7 min-w-11 sm:min-w-7 shrink-0 inline-flex items-center justify-center"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <input
                                  type="checkbox"
                                  aria-label={t('fileBrowser.selectItem', { name: file.name })}
                                  checked={selectedFiles.includes(file.path)}
                                  onChange={() => toggleFileSelection(file.path)}
                                  className="h-4 w-4 text-brand-600 dark:text-brand-400 focus:ring-brand-500 border-border-strong-light dark:border-border-strong-dark rounded"
                                />
                              </label>
                              <svg
                                className="h-5 w-5 shrink-0 text-state-warn-light dark:text-state-warn-dark"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                              </svg>
                              <div className="min-w-0 flex-1">
                                <div className="flex min-w-0 flex-col sm:flex-row sm:items-center sm:justify-between">
                                  <span className="min-w-0 break-words text-sm font-bold text-slate-900 dark:text-slate-200">
                                    {file.name}
                                  </span>
                                  {file.parent_path && (
                                    <span className="break-all text-xs text-text-muted-light dark:text-text-muted-dark font-medium">
                                      {t('fileBrowser.location')}: {file.parent_path}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </>
                          ) : (
                            <>
                              <label
                                className="min-h-11 sm:min-h-7 min-w-11 sm:min-w-7 shrink-0 inline-flex items-center justify-center"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <input
                                  type="checkbox"
                                  aria-label={t('fileBrowser.selectItem', { name: file.name })}
                                  checked={selectedFiles.includes(file.path)}
                                  onChange={() => toggleFileSelection(file.path)}
                                  className="h-4 w-4 text-brand-600 dark:text-brand-400 focus:ring-brand-500 border-border-strong-light dark:border-border-strong-dark rounded"
                                />
                              </label>
                              <svg
                                className="h-5 w-5 shrink-0 text-text-subtle-light dark:text-text-subtle-dark"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"
                                  clipRule="evenodd"
                                />
                              </svg>
                              <div className="min-w-0 flex-1">
                                <div className="flex min-w-0 flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                                  <span className="min-w-0 break-words text-sm font-bold text-slate-900 dark:text-slate-200">
                                    {file.name}
                                  </span>
                                  <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-text-muted-light dark:text-text-muted-dark sm:items-center sm:space-x-4">
                                    {file.parent_path && (
                                      <span>
                                        {t('fileBrowser.location')}: {file.parent_path}
                                      </span>
                                    )}
                                    <span>{formatBytes(file.size)}</span>
                                    <span>{formatTimestamp(file.modified)}</span>
                                  </div>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </>
              )}
            </div>
          ) : (
            /* Browse Mode */
            <>
              {loading ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
                  <p className="mt-2 text-sm text-text-muted-light dark:text-text-muted-dark font-medium">
                    {t('fileBrowser.loading')}
                  </p>
                </div>
              ) : (
                <div className="overflow-hidden border border-surface-border-light dark:border-surface-border-dark rounded-xl bg-surface-2-light dark:bg-surface-2-dark">
                  {/* Navigate Up Button */}
                  {currentPath !== '/' && currentPath !== 'C:\\' && currentPath !== 'C:/' && (
                    <button
                      type="button"
                      aria-label={t('fileBrowser.parentDirectory')}
                      className="min-h-11 sm:min-h-7 w-full flex items-center px-3 py-0 hover:bg-surface-2-light dark:hover:bg-surface-border-dark border-b border-surface-border-light dark:border-surface-border-dark text-left text-slate-600 dark:text-slate-300 transition-colors"
                      onClick={handleNavigateUp}
                    >
                      <svg
                        className="h-5 w-5 text-text-subtle-light dark:text-text-subtle-dark mr-3"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 15l7-7 7 7"
                        />
                      </svg>
                      <span className="text-sm font-bold">{t('fileBrowser.parentDirectory')}</span>
                    </button>
                  )}

                  {/* Files and Directories */}
                  {files.length === 0 ? (
                    <div className="text-center py-8 text-text-muted-light dark:text-text-muted-dark font-medium">
                      {t('fileBrowser.emptyDirectory')}
                    </div>
                  ) : (
                    files.map((file) => (
                      <div
                        key={file.path}
                        className={`flex min-h-11 sm:min-h-7 min-w-0 items-center gap-2 sm:gap-3 px-2 sm:px-3 py-0 border-b border-surface-border-light dark:border-surface-border-dark last:border-b-0 hover:bg-surface-2-light dark:hover:bg-surface-border-dark transition-colors ${
                          file.type === 'directory' ? 'cursor-pointer' : ''
                        }`}
                        onClick={() => {
                          if (file.type === 'directory' && !selectedFiles.includes(file.path)) {
                            handleNavigateToPath(file.path);
                          }
                        }}
                      >
                        {file.type === 'directory' ? (
                          <>
                            <label
                              className="min-h-11 sm:min-h-7 min-w-11 sm:min-w-7 shrink-0 inline-flex items-center justify-center"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <input
                                type="checkbox"
                                aria-label={t('fileBrowser.selectItem', { name: file.name })}
                                checked={selectedFiles.includes(file.path)}
                                onChange={() => toggleFileSelection(file.path)}
                                className="h-4 w-4 text-brand-600 dark:text-brand-400 focus:ring-brand-500 border-border-strong-light dark:border-border-strong-dark rounded"
                              />
                            </label>
                            <svg
                              className="h-5 w-5 shrink-0 text-state-warn-light dark:text-state-warn-dark"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                            </svg>
                            <div className="min-w-0 flex-1">
                              <div className="flex min-w-0 flex-col sm:flex-row sm:items-center sm:justify-between">
                                <button
                                  type="button"
                                  aria-label={t('fileBrowser.openFolder', { name: file.name })}
                                  className="min-h-11 sm:min-h-7 min-w-0 break-words text-left text-sm font-bold text-slate-900 dark:text-slate-200"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    if (!selectedFiles.includes(file.path)) {
                                      handleNavigateToPath(file.path);
                                    }
                                  }}
                                >
                                  {file.name}
                                </button>
                                <span className="text-xs text-brand-600 dark:text-brand-400 font-medium">
                                  {t('fileBrowser.clickToBrowse')}
                                </span>
                              </div>
                            </div>
                          </>
                        ) : (
                          <>
                            <label
                              className="min-h-11 sm:min-h-7 min-w-11 sm:min-w-7 shrink-0 inline-flex items-center justify-center"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <input
                                type="checkbox"
                                aria-label={t('fileBrowser.selectItem', { name: file.name })}
                                checked={selectedFiles.includes(file.path)}
                                onChange={() => toggleFileSelection(file.path)}
                                className="h-4 w-4 text-brand-600 dark:text-brand-400 focus:ring-brand-500 border-border-strong-light dark:border-border-strong-dark rounded"
                              />
                            </label>
                            <svg
                              className="h-5 w-5 shrink-0 text-text-subtle-light dark:text-text-subtle-dark"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"
                                clipRule="evenodd"
                              />
                            </svg>
                            <div className="min-w-0 flex-1">
                              <div className="flex min-w-0 flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                                <span className="min-w-0 break-words text-sm font-bold text-slate-900 dark:text-slate-200">
                                  {file.name}
                                </span>
                                <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-text-muted-light dark:text-text-muted-dark sm:items-center sm:space-x-4">
                                  <span>{formatBytes(file.size)}</span>
                                  <span>{formatTimestamp(file.modified)}</span>
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}
            </>
          )}

          {/* Selection Summary */}
          {selectedFiles.length > 0 && (
            <div className="mt-4 bg-accent-soft-light dark:bg-accent-soft-dark border border-accent-text-light/20 dark:border-accent-text-dark/20 rounded-xl p-4">
              <p className="text-sm text-accent-text-light dark:text-accent-text-dark font-medium">
                <span className="font-black">{selectedFiles.length}</span>{' '}
                {t('fileBrowser.itemsSelected')}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 sm:p-6 border-t border-surface-border-light dark:border-surface-border-dark flex flex-col-reverse sm:flex-row sm:justify-end gap-3 flex-shrink-0 bg-surface-2-light dark:bg-surface-2-dark">
          <button
            type="button"
            onClick={onClose}
            className="min-h-11 px-4 py-2 border border-border-strong-light dark:border-border-strong-dark rounded-xl text-slate-700 dark:text-slate-300 hover:bg-surface-2-light dark:hover:bg-surface-border-dark font-black uppercase tracking-wider text-sm transition-colors"
          >
            {t('fileBrowser.cancel')}
          </button>
          <button
            type="button"
            onClick={handleConfirmSelection}
            disabled={selectedFiles.length === 0}
            className="min-h-11 px-4 py-2 bg-brand-600 text-white rounded-xl hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed font-black uppercase tracking-wider text-sm transition-colors shadow-lg shadow-brand-200 dark:shadow-none"
          >
            {t('fileBrowser.restoreSelected', { count: selectedFiles.length })}
          </button>
        </div>
      </div>
    </div>
  );
};
